import { readBoolean, readDateIso, readString } from "./mapperUtils.js";

export type CmtsRcaVm = Record<string, unknown> & {
  id: string;
  rcaId: string;
  complaintId: string;
  method: string;
  problemStatement: string;
  rootCause: string;
  rootCauseCategory: string;
  containment: string;
  status: string;
  ownerId: string;
  completed: boolean;
  completedAt?: string | undefined;
};

export type CmtsCapaVm = Record<string, unknown> & {
  id: string;
  capaId: string;
  complaintId: string;
  actionType: string;
  description: string;
  ownerId: string;
  dueDate: string;
  status: string;
  effectivenessRequired: boolean;
  effectivenessStatus: string;
  effectivenessCheck: string;
};

function asRecord(dto: unknown): Record<string, unknown> {
  return dto && typeof dto === "object" && !Array.isArray(dto) ? dto as Record<string, unknown> : {};
}

export function mapRca(dto: unknown): CmtsRcaVm {
  const row = asRecord(dto);
  const id = readString(dto, ["id", "rcaId"], "");
  const rcaId = readString(dto, ["rcaId", "id"], id);
  return {
    ...row,
    id,
    rcaId,
    complaintId: readString(dto, ["complaintId", "linkedEntityId"], ""),
    method: readString(dto, ["method", "rcaMethod"], "Not specified"),
    problemStatement: readString(dto, ["problemStatement", "statement"], "Not provided"),
    rootCause: readString(dto, ["rootCause", "cause", "confirmedRootCause"], "Not identified"),
    rootCauseCategory: readString(dto, ["categoryName", "rootCauseCategoryName", "categoryCode", "rootCauseCategory", "category"], "Uncategorized"),
    containment: readString(dto, ["containment", "containmentAction", "containmentConfirmation", "containmentSummary"], ""),
    status: readString(dto, ["status"], "OPEN"),
    ownerId: readString(dto, ["ownerId", "ownerUserId"], "unassigned"),
    ownerUserId: readString(dto, ["ownerUserId", "ownerId"], "unassigned"),
    completed: readBoolean(dto, ["completed"], false),
    completedAt: readDateIso(dto, ["completedAt", "completedAtUtc"], ""),
  };
}

export function mapCapa(dto: unknown): CmtsCapaVm {
  const row = asRecord(dto);
  const id = readString(dto, ["id", "capaId"], "");
  const capaId = readString(dto, ["capaId", "id"], id);
  return {
    ...row,
    id,
    capaId,
    complaintId: readString(dto, ["complaintId", "linkedEntityId"], ""),
    actionType: readString(dto, ["actionType", "type"], "CORRECTIVE"),
    description: readString(dto, ["description", "actionDescription", "correctiveAction"], "Not provided"),
    ownerId: readString(dto, ["ownerId", "ownerUserId"], "unassigned"),
    ownerUserId: readString(dto, ["ownerUserId", "ownerId"], "unassigned"),
    dueDate: readDateIso(dto, ["dueDate", "dueAtUtc", "dueAt"], ""),
    status: readString(dto, ["status"], "OPEN"),
    effectivenessRequired: readBoolean(dto, ["effectivenessRequired"], true),
    effectivenessStatus: readString(dto, ["effectivenessStatus"], "Not verified"),
    effectivenessCheck: readString(dto, ["effectivenessCheck", "effectivenessCriteria", "verificationPlan"], ""),
  };
}

export function mapComplaintToRcaQueue(dto: unknown): CmtsRcaVm {
  const row = asRecord(dto);
  const complaintId = readString(dto, ["complaintId", "id", "linkedEntityId"], "");
  const title = readString(dto, ["title", "summary", "problemStatement"], "Complaint pending RCA review");
  return {
    ...row,
    id: complaintId ? `pending-rca-${complaintId}` : "pending-rca-missing-complaint",
    queueId: complaintId ? `pending-rca-${complaintId}` : "pending-rca-missing-complaint",
    rcaId: "",
    complaintId,
    persisted: false,
    method: "Pending selection",
    problemStatement: title,
    rootCause: "Not identified",
    rootCauseCategory: "-",
    containment: readString(dto, ["containment", "containmentStatus"], ""),
    status: "PENDING_RCA",
    ownerId: readString(dto, ["ownerId", "complaintOwnerId", "assignedToUserId"], "unassigned"),
    ownerUserId: readString(dto, ["ownerUserId", "ownerId", "complaintOwnerId"], "unassigned"),
    completed: false,
    completedAt: "",
    recordKind: "complaint-pending-rca",
    linkedEntityId: complaintId,
    title,
  };
}

export function mapComplaintToCapaQueue(dto: unknown): CmtsCapaVm {
  const row = asRecord(dto);
  const complaintId = readString(dto, ["complaintId", "id", "linkedEntityId"], "");
  const title = readString(dto, ["title", "summary", "actionDescription"], "Complaint pending CAPA review");
  return {
    ...row,
    id: complaintId ? `pending-capa-${complaintId}` : "pending-capa-missing-complaint",
    queueId: complaintId ? `pending-capa-${complaintId}` : "pending-capa-missing-complaint",
    capaId: "",
    complaintId,
    persisted: false,
    actionType: "Pending selection",
    description: title,
    actionDescription: title,
    ownerId: readString(dto, ["ownerId", "complaintOwnerId", "assignedToUserId"], "unassigned"),
    ownerUserId: readString(dto, ["ownerUserId", "ownerId", "complaintOwnerId"], "unassigned"),
    dueDate: readDateIso(dto, ["resolutionDueAt", "dueAt", "slaDueDate"], ""),
    status: "PENDING_CAPA",
    effectivenessRequired: true,
    effectivenessStatus: "Not verified",
    effectivenessCheck: "",
    recordKind: "complaint-pending-capa",
    linkedEntityId: complaintId,
    title,
  };
}
